export const XP_ICON_SRC = "/assets/xp.webp";

export function formatXp(value: number): string {
  const n = Number(value) || 0;
  return new Intl.NumberFormat("en-IN").format(n);
}

export function formatXpRank(rank: number | null | undefined): string {
  const r = Math.trunc(Number(rank) || 0);
  if (r <= 0) return "—";
  const mod100 = r % 100;
  const mod10 = r % 10;
  if (mod100 >= 11 && mod100 <= 13) return `${r}TH`;
  if (mod10 === 1) return `${r}ST`;
  if (mod10 === 2) return `${r}ND`;
  if (mod10 === 3) return `${r}RD`;
  return `${r}TH`;
}

export function formatWeekCountdown(countdown: {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
} | null | undefined): string {
  if (!countdown) return "";
  const d = Math.max(0, Number(countdown.days) || 0);
  const h = Math.max(0, Number(countdown.hours) || 0);
  const m = Math.max(0, Number(countdown.minutes) || 0);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}D`);
  if (h > 0) parts.push(`${h}H`);
  if (m > 0 || parts.length === 0) parts.push(`${m}M`);
  return parts.join(" ");
}

export type XpWeekOption = {
  week_key: string;
  label: string;
  is_current: boolean;
  finalized: boolean;
};

export type XpLeaderboardResponse = {
  leaderboard: Array<Record<string, unknown>>;
  weekKey?: string | null;
  weekLabel?: string | null;
  isCurrentWeek?: boolean;
  weekOptions?: XpWeekOption[];
  countdown?: { days?: number; hours?: number; minutes?: number; seconds?: number } | null;
  periodTimezoneLabel?: string;
};

export function parseXpWeekOptions(raw: unknown): XpWeekOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const week_key = String(o.week_key ?? "").trim();
      if (!week_key) return null;
      return {
        week_key,
        label: String(o.label ?? week_key),
        is_current: Boolean(o.is_current),
        finalized: Boolean(o.finalized),
      };
    })
    .filter((x): x is XpWeekOption => x != null);
}

export type XpSummary = {
  total_xp: number;
  week_xp: number;
  week_rank: number | null;
  login_streak?: number;
  weekly_finishes: { first: number; second: number; third: number };
};

// XP week is anchored to Monday 00:00 IST (fixed; the leaderboard finalizes weekly on Mondays).
const XP_WEEK_TZ = "Asia/Kolkata";
const XP_WEEK_ANCHOR_DOW = 1;
const XP_WEEK_ANCHOR_HOUR = 0;
const XP_WEEK_ANCHOR_MINUTE = 0;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function zonedWallClockToUtc(
  y: number,
  monthIndex: number,
  d: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): Date {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  let ms = Date.UTC(y, monthIndex, d, hour, minute, second);
  for (let i = 0; i < 4; i++) {
    const formatted = Object.fromEntries(
      dtf
        .formatToParts(new Date(ms))
        .filter((p) => p.type !== "literal")
        .map((p) => [p.type, parseInt(p.value, 10)])
    ) as Record<string, number>;
    const formattedMs = Date.UTC(
      formatted.year,
      formatted.month - 1,
      formatted.day,
      formatted.hour,
      formatted.minute,
      formatted.second
    );
    const targetMs = Date.UTC(y, monthIndex, d, hour, minute, second);
    const delta = targetMs - formattedMs;
    ms += delta;
    if (delta === 0) break;
  }
  return new Date(ms);
}

function getZonedDateTimeParts(now: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(now).filter((p) => p.type !== "literal").map((p) => [p.type, p.value])
  );
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    y: parseInt(parts.year, 10),
    m: parseInt(parts.month, 10) - 1,
    d: parseInt(parts.day, 10),
    dow: dayMap[parts.weekday] ?? 0,
  };
}

/** Current XP week start (most recent Monday 00:00 IST) — matches the backend weekly cadence. */
export function getXpWeekStart(now = new Date()): Date {
  const parts = getZonedDateTimeParts(now, XP_WEEK_TZ);
  const daysFromAnchor = (parts.dow - XP_WEEK_ANCHOR_DOW + 7) % 7;
  const anchorCalendar = new Date(Date.UTC(parts.y, parts.m, parts.d));
  anchorCalendar.setUTCDate(anchorCalendar.getUTCDate() - daysFromAnchor);
  let weekStart = zonedWallClockToUtc(
    anchorCalendar.getUTCFullYear(),
    anchorCalendar.getUTCMonth(),
    anchorCalendar.getUTCDate(),
    XP_WEEK_ANCHOR_HOUR,
    XP_WEEK_ANCHOR_MINUTE,
    0,
    XP_WEEK_TZ
  );
  if (weekStart.getTime() > now.getTime()) {
    weekStart = new Date(weekStart.getTime() - WEEK_MS);
  }
  return weekStart;
}

const TRADER_SPORT_DISPLAY: Record<string, string> = {
  cricket: "Cricket",
  horses: "Horses",
  football: "Football",
  tennis: "Tennis",
};

/** Sport segment from `trader_daily_coins_first:SPORT:YYYY-MM-DD` XP reasons. */
export function parseTraderDailyLeaderboardSportFromReason(
  reason: string | null | undefined
): string | null {
  const parts = String(reason ?? "")
    .trim()
    .split(":");
  if (parts[0]?.toLowerCase() !== "trader_daily_coins_first" || parts.length < 2) {
    return null;
  }
  const sport = parts[1]?.trim();
  return sport || null;
}

/** IST day segment from `trader_daily_coins_first:SPORT:YYYY-MM-DD`. */
export function parseTraderDailyLeaderboardDayFromReason(
  reason: string | null | undefined
): string | null {
  const parts = String(reason ?? "")
    .trim()
    .split(":");
  if (parts[0]?.toLowerCase() !== "trader_daily_coins_first" || parts.length < 3) {
    return null;
  }
  const day = parts[2]?.trim();
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

export function formatTraderSportDisplayName(sport: string | null | undefined): string | null {
  const key = String(sport ?? "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  return TRADER_SPORT_DISPLAY[key] ?? `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

function formatTraderDailyLeaderboardDayLabel(dayKey: string | null | undefined): string | null {
  if (!dayKey) return null;
  const d = new Date(`${dayKey}T12:00:00+05:30`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
}

/** Activity / XP history parenthetical, e.g. "(Daily Cricket Trader leaderboard win, 12 Jun)". */
export function formatTraderDailyLeaderboardXpDetail(reason: string | null | undefined): string {
  const sport = formatTraderSportDisplayName(parseTraderDailyLeaderboardSportFromReason(reason));
  const day = formatTraderDailyLeaderboardDayLabel(parseTraderDailyLeaderboardDayFromReason(reason));
  if (sport && day) return `(Daily ${sport} Trader leaderboard win, ${day})`;
  if (sport) return `(Daily ${sport} Trader leaderboard win)`;
  return "(Daily trader leaderboard win)";
}

/** Notification phrase after "for …", e.g. "topping the 12 Jun Cricket Trader daily leaderboard". */
export function formatTraderDailyLeaderboardXpNotificationDetail(
  reason: string | null | undefined
): string {
  const sport = formatTraderSportDisplayName(parseTraderDailyLeaderboardSportFromReason(reason));
  const day = formatTraderDailyLeaderboardDayLabel(parseTraderDailyLeaderboardDayFromReason(reason));
  if (sport && day) return `topping the ${day} ${sport} Trader daily leaderboard`;
  if (sport) return `topping the daily ${sport} Trader leaderboard`;
  return "topping the daily Trader leaderboard";
}

/** IST day segment from `sixes_daily_coins_first:YYYY-MM-DD`. */
export function parseSixesDailyLeaderboardDayFromReason(
  reason: string | null | undefined
): string | null {
  const parts = String(reason ?? "")
    .trim()
    .split(":");
  if (parts[0]?.toLowerCase() !== "sixes_daily_coins_first" || parts.length < 2) {
    return null;
  }
  const day = parts[1]?.trim();
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/** Activity / XP history parenthetical, e.g. "(Daily Sixes leaderboard win, 12 Jun)". */
export function formatSixesDailyLeaderboardXpDetail(reason: string | null | undefined): string {
  const day = formatTraderDailyLeaderboardDayLabel(parseSixesDailyLeaderboardDayFromReason(reason));
  if (day) return `(Daily Sixes leaderboard win, ${day})`;
  return "(Daily Sixes leaderboard win)";
}

/** Notification phrase after "for …", e.g. "topping the 12 Jun Sixes daily leaderboard". */
export function formatSixesDailyLeaderboardXpNotificationDetail(
  reason: string | null | undefined
): string {
  const day = formatTraderDailyLeaderboardDayLabel(parseSixesDailyLeaderboardDayFromReason(reason));
  if (day) return `topping the ${day} Sixes daily leaderboard`;
  return "topping the daily Sixes leaderboard";
}

export function formatXpHistoryRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return "Just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return `${Math.floor(sec / 604800)}w`;
}

const LEAGUE_XP_GAME_LABELS: Record<string, string> = {
  trader: "Trader",
  squares: "Squares+",
  sixes: "Sixes",
  bullion: "Bullion+",
};

function formatLeaguePlaceSuffix(place: number): string {
  const mod100 = place % 100;
  let suffix = "th";
  if (mod100 < 11 || mod100 > 13) {
    const mod10 = place % 10;
    if (mod10 === 1) suffix = "st";
    else if (mod10 === 2) suffix = "nd";
    else if (mod10 === 3) suffix = "rd";
  }
  return `${place}${suffix}`;
}

/** Parenthetical XP history copy for league_* reasons (supports `:count` / `:place` / `:game` suffixes). */
export function formatLeagueXpHistoryDetail(reason: string | null | undefined): string | null {
  const raw = String(reason ?? "").trim();
  const parts = raw.toLowerCase().split(":");
  const base = parts[0] ?? "";
  if (base === "league_participation") {
    return "(Finished a League)";
  }
  if (base === "league_game_played") {
    const n = Number.parseInt(String(parts[1] ?? ""), 10);
    if (Number.isFinite(n) && n > 0) {
      return n === 1 ? "(Played 1 League game)" : `(Played ${n} League games)`;
    }
    return "(Played League games)";
  }
  if (base === "league_points") {
    const points = Number.parseInt(String(parts[1] ?? ""), 10);
    if (Number.isFinite(points) && points >= 0) {
      return `(${points.toLocaleString("en-US")} pts from League play)`;
    }
    return "(League play)";
  }
  if (base === "league_game_win") {
    const game = String(parts[1] ?? "");
    const label = LEAGUE_XP_GAME_LABELS[game] ?? (game ? game : null);
    return label ? `(${label} game win)` : "(League game win)";
  }
  if (base === "league_placement") {
    const place = Number.parseInt(String(parts[1] ?? ""), 10);
    if (place === 1) return "(Won a League)";
    if (place === 2) return "(2nd place in a League)";
    if (place === 3) return "(3rd place in a League)";
    if (Number.isFinite(place) && place > 0) {
      return `(${formatLeaguePlaceSuffix(place)} place in a League)`;
    }
    return "(League podium finish)";
  }
  return null;
}

export function formatXpHistoryReasonDetail(reason: string | null | undefined): string {
  const raw = String(reason ?? "activity").trim();
  const key = raw.toLowerCase();
  const base = key.split(":")[0] ?? key;
  if (base === "trader_daily_coins_first") {
    return formatTraderDailyLeaderboardXpDetail(raw);
  }
  if (base === "sixes_daily_coins_first") {
    return formatSixesDailyLeaderboardXpDetail(raw);
  }
  const leagueDetail = formatLeagueXpHistoryDetail(raw);
  if (leagueDetail) return leagueDetail;
  const labels: Record<string, string> = {
    signup: "(New account)",
    login_daily: "(Daily login)",
    login_streak_4: "(4-day login streak)",
    login_streak_8: "(8-day login streak)",
    wheel_spin_daily: "(Daily Free Coins)",
    wheel_multiplier_home: "(Wheel Trivia multiplier)",
    wheel_multiplier_away: "(Wheel Trivia multiplier)",
    trader_win: "(Trader win)",
    trader_all_in_first: "(First all-in)",
    trader_win_streak_4: "(4-trade win streak)",
    trader_win_streak_8: "(8-trade win streak)",
    trader_match_wins_3: "(3 wins in a match)",
    trader_match_wins_5: "(5 wins in a match)",
    squares_play_over: "(Play a Squares over)",
    squares_win_over: "(Win a Squares over)",
    squares_five_balls: "(5 balls correct in Squares)",
    squares_perfect_over: "(Perfect Squares over)",
    sixes_play_card: "(Played a Sixes game)",
    sixes_coins_won: "(Sixes coins won)",
    bullion_play_private: "(Private Bullion game played)",
    bullion_play_public: "(Public Bullion game played)",
    bullion_win_private_first: "(Private Bullion win)",
    bullion_win_public_first: "(Public Bullion win)",
    bullion_place_private: "(Private Bullion place prize)",
    bullion_place_public: "(Public Bullion place prize)",
    bullion_predictions_80pct: "(80%+ Bullion predictions)",
    shop_rubies_3: "(3-pack Rubies purchase)",
    shop_rubies_10: "(10-pack Rubies purchase)",
    shop_rubies_25: "(25-pack Rubies purchase)",
    shop_feature_pack: "(Profile Unlock Pack)",
    shop_first_ruby_pack: "(First Ruby pack purchase)",
    social_first_friend: "(First friend added)",
    social_invite_new_user: "(Invited a new user)",
    social_friend_existing: "(New friend)",
    social_favourite_team: "(Favourite team on profile)",
    social_favourite_player: "(Favourite player on profile)",
    social_favourite_sport: "(Favourite sport on profile)",
    social_country: "(Country on profile)",
    social_player_name: "(Player name saved)",
    social_avatar: "(Avatar chosen)",
    wheel_multiplier_streak_4: "(Multiplier streak)",
    wheel_multiplier_streak_8: "(Multiplier streak)",
    quickfire_correct: "(Correct answer)",
    quickfire_daily_perfect: "(Perfect Quickfire day)",
    quickfire: "(Quickfire)",
    squares: "(Squares)",
    bullion: "(Bullion)",
    trader: "(Trader)",
    activity: "(Activity)",
  };
  const label = labels[base] ?? labels[key] ?? `(${base.replace(/_/g, " ")})`;
  return label.startsWith("(") ? label : `(${label})`;
}

export function parseXpSummary(raw: unknown): XpSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const root = raw as Record<string, unknown>;
  const o =
    root.xp && typeof root.xp === "object" && !Array.isArray(root.xp)
      ? (root.xp as Record<string, unknown>)
      : root;
  const fin =
    o.weekly_finishes && typeof o.weekly_finishes === "object"
      ? (o.weekly_finishes as Record<string, unknown>)
      : root.weekly_finishes && typeof root.weekly_finishes === "object"
        ? (root.weekly_finishes as Record<string, unknown>)
        : {};
  const weekRankRaw = o.week_rank ?? o.weekRank;
  const loginStreakRaw = o.login_streak ?? o.loginStreak ?? root.login_streak;
  return {
    total_xp: Number(o.total_xp ?? o.totalXp) || 0,
    week_xp: Number(o.week_xp ?? o.weekXp) || 0,
    week_rank:
      weekRankRaw != null && weekRankRaw !== ""
        ? Number(weekRankRaw) || null
        : null,
    login_streak:
      loginStreakRaw != null && loginStreakRaw !== ""
        ? Number(loginStreakRaw) || 0
        : undefined,
    weekly_finishes: {
      first: Number(fin.first ?? fin.first_place) || 0,
      second: Number(fin.second ?? fin.second_place) || 0,
      third: Number(fin.third ?? fin.third_place) || 0,
    },
  };
}

/** Merge incoming XP summary without downgrading week totals (e.g. partial career payloads). */
export function mergeXpSummaryPreferHigher(
  prev: XpSummary | null,
  next: XpSummary | null
): XpSummary | null {
  if (!next) return prev;
  if (!prev) return next;
  const week_xp = Math.max(prev.week_xp, next.week_xp);
  const week_rank =
    next.week_rank != null && next.week_rank > 0
      ? next.week_rank
      : week_xp > 0 && prev.week_rank != null && prev.week_rank > 0
        ? prev.week_rank
        : next.week_rank ?? prev.week_rank;
  return {
    total_xp: Math.max(prev.total_xp, next.total_xp),
    week_xp,
    week_rank,
    login_streak: next.login_streak ?? prev.login_streak,
    weekly_finishes: next.weekly_finishes,
  };
}

type LeaderboardMeRow = { isCurrentUser?: boolean; xp?: number; rank?: number | null };

/**
 * My Zone banner: load `/account/xp-summary`, then fall back to the week leaderboard row for the viewer.
 */
export async function fetchMyZoneXpSummary(
  apiUrl: string,
  headers: HeadersInit,
  walletXp?: number | null
): Promise<XpSummary | null> {
  const base = apiUrl.replace(/\/$/, "");
  let summary: XpSummary | null = null;

  try {
    const res = await fetch(`${base}/account/xp-summary`, { method: "GET", headers });
    if (res.ok) {
      summary = parseXpSummary(await res.json());
    }
  } catch {
    // keep summary null
  }

  const needsLeaderboard =
    !summary || summary.week_xp <= 0 || summary.week_rank == null || summary.week_rank <= 0;

  if (!needsLeaderboard) return summary;

  try {
    const lbRes = await fetch(`${base}/xp/leaderboard?period=week&scope=all`, { headers });
    if (!lbRes.ok) return summary;
    const lb = (await lbRes.json()) as {
      leaderboard?: LeaderboardMeRow[];
      userRank?: number | null;
    };
    const rows = Array.isArray(lb.leaderboard) ? lb.leaderboard : [];
    const me = rows.find((r) => r.isCurrentUser);
    const meXp = me != null ? Number(me.xp) || 0 : 0;
    const meRank = me != null ? Number(me.rank) || 0 : 0;
    const userRank = typeof lb.userRank === "number" && lb.userRank > 0 ? lb.userRank : null;
    const week_xp = Math.max(summary?.week_xp ?? 0, meXp);
    const week_rank = meRank > 0 ? meRank : userRank ?? summary?.week_rank ?? null;
    if (week_xp <= 0 && (week_rank == null || week_rank <= 0)) return summary;
    return {
      total_xp: summary?.total_xp ?? (walletXp != null ? Number(walletXp) || 0 : 0),
      week_xp,
      week_rank,
      login_streak: summary?.login_streak,
      weekly_finishes: summary?.weekly_finishes ?? { first: 0, second: 0, third: 0 },
    };
  } catch {
    return summary;
  }
}
